import React, { useCallback, useMemo, useState, useEffect } from "react";
import { classNames } from "../../common_util";
import { Product } from "../types/catalog";
import { useCart, useModal, useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { NavigationState, serializeShopState, StorefrontPage } from "../types/navigation";
import { CartItem } from "src/storefront/types/cart";
import * as CM from "src/storefront/types/cart";
import { StreamlineIcon } from "src/components/icons";
import addIconUrl from "images/icons/add.svg";
import pencilIconUrl from "images/icons/pencil.svg";
import subtractIconUrl from "images/icons/subtract.svg";

export const ProductListingGridElement = ({
  product,
  addToCart,
  removeFromCart,
  updateCart
}: {
  product: Product;
  addToCart?: (newCartItem: CM.CartItem, index?: number) => Promise<CM.Cart>;
  removeFromCart: (itemToRemove: CM.CartItem) => Promise<CM.Cart>;
  updateCart: (updatedCartItem: CM.CartItem) => Promise<CM.Cart>;
}): React.JSX.Element => {
  const config = useStorefrontConfig();
  const showModal = useModal();
  const cart = useCart();

  const { isInFulfillableTimeRange, isOrderable, getPrimaryImageUrl } = useStorefrontHelper();
  const [displayQuantityBar, setDisplayQuantityBar] = useState<boolean>(false);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [hovering, setHovering] = useState<boolean>(false);

  const priceDisplay = product.price ? config.priceFormatter.format(product.price / 100.0) : "";

  const countInCart = cart?.getCountOfItemInCart(product) || 0;

  const isAvailable = config.presentationStatus == "open_for_business" && isInFulfillableTimeRange(product) && isOrderable(product);
  const hideOutOfStockProductListing = !isOrderable(product) && config.hideOutOfStockItems;

  useEffect(() => {
    const intervalId = setInterval(() => {
      // 3secs atm, open to ideas on this.
      if (!hovering && displayQuantityBar && (Date.now() - lastClickTime > 3000)) {
        setDisplayQuantityBar(false);
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [displayQuantityBar, lastClickTime, hovering]);

  const handleMouseInForQuantityBar = useCallback(() => {
    setHovering(true);
  }, [])

  const handleMouseOutForQuantityBar = useCallback(() => {
    setHovering(false);
    setLastClickTime(Date.now())
  },[])

  const productModalOnClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    showModal(product, true);
  }, [product, showModal])

  const classes = classNames({
    "product-listing-is-grid": true,
    "is-available": isAvailable,
    "is-unavailable": !isAvailable,
  });

  const addItemToCart = useCallback(async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (addToCart) {
      if (product.kind == "leaf" || product.kind == "branch") {

        if (product.kind == "branch") { showModal(product, true) }

        if (product.kind == "leaf") {
          await addToCart(CartItem.fromProduct(product))
          setLastClickTime(Date.now());
          setDisplayQuantityBar(true);
        }
      }
    }
  },[addToCart, product, showModal]);

  const decrementCartItemQuantity = useCallback(async (event: React.MouseEvent, product: Product) => {
    event.preventDefault();
    event.stopPropagation();

    const matchingCartItem = cart?.cartItems.filter((ci) => {
      return JSON.stringify(ci.keyPrefix) === JSON.stringify(product.keyPrefix) && product.key === ci.key
    })

    if (!matchingCartItem) return;
    const cartItemToUpdate = matchingCartItem[matchingCartItem.length - 1].copyWith({ quantity: matchingCartItem[matchingCartItem.length -1].quantity - 1 })

    if (cart?.getCountOfItemInCart(product) === 1) {
      await removeFromCart(cartItemToUpdate)
      setLastClickTime(0)
      setDisplayQuantityBar(false)
    } else {
      await updateCart(cartItemToUpdate)
      setLastClickTime(Date.now())
    }
  }, [removeFromCart, updateCart, cart])

  const onEditClickDisplayOption = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setLastClickTime(Date.now());

    setDisplayQuantityBar(true);
  }, [])

  const productDetails = useMemo(() => {
    const title = product.titleWithoutSizeLabel();

    return <div className="product-details" title={title}>
      <p>
        <span>{title}</span>
        {product.formattedSizeLabel() ? <span>{product.formattedSizeLabel()}</span> : null}
      </p>
    </div>
  }, [product]);

  const productImage = <div
    className="product-image"
    data-lazy-images-target="image"
    data-lazy-images-imagery-variant="210x210"
    data-lazy-images-imagery-path-value={getPrimaryImageUrl(product)}
  >
  </div>;

  const productPrice = <div className="price">
    {priceDisplay}
  </div>;

  const unitPriceLabel = <>
    {product.unitPriceLabel ? <div className="is-tiny-text is-muted-text space-mb-none">{product.unitPriceLabel}</div> : <></>}
  </>

  const quickAddToCartButton = useMemo(() => {
    return <button
      role="button"
      aria-label="Quick add"
      className="button is-tiny is-icon-only has-plain-icon is-raised"
      data-testid="quick-add-button"
      onClick={addItemToCart}
    >
      <StreamlineIcon href={addIconUrl} />
    </button>
  }, [addItemToCart]);

  const removeItemFromCartButton = useMemo(() => {
    return countInCart >= 1 ?
     <button
        role="button"
        aria-label="Remove 1 from cart"
        className="button is-tiny is-icon-only has-plain-icon is-raised"
        data-cy="quick-remove-from-cart"
        onClick={event => decrementCartItemQuantity(event, product)}
        disabled={!cart?.getCountOfItemInCart(product)}
      >
        <StreamlineIcon href={subtractIconUrl} />
      </button>
    : <></>
  },[cart, product, countInCart, decrementCartItemQuantity]);

  const editCartQuantity = useMemo(() => {
    return <button
      role="button"
      aria-label="Remove 1 from cart"
      className="button is-tiny is-icon-only has-plain-icon is-raised"
      data-testid="edit-quantity-for-cart"
      onClick={event => onEditClickDisplayOption(event)}
    >
      <StreamlineIcon href={pencilIconUrl} />
    </button>
  }, [onEditClickDisplayOption]);

  const outOfStockOrRestrictionBadge = <>
    {!isOrderable(product) && (config.fulfillmentMethod == 'pickup' && product.restrictions?.includes('age_r18')) ? <><p className="space-mb-none">Delivery only</p></> : <div className="badge is-strong"><span>Out</span></div>}
  </>

  const quantityButton = useMemo(() => {
    return countInCart >= 1 ? <div className="badge is-quantity is-round is-tiny" data-testid="quantity-tag">{cart?.getCountOfItemInCart(product)}</div> : <></>
  }, [cart, product, countInCart])

  const fakeLinkHref = `#${serializeShopState(new NavigationState(StorefrontPage.Shop, "", [], product.fullKeyPath(), {}))}`;

  const displayQuantityOptions = useMemo(() => {
    return displayQuantityBar && countInCart >= 1 ? (
      <div className="is-flexbox justify-content-between align-items-center flex-grow-1" onMouseEnter={handleMouseInForQuantityBar} onMouseLeave={handleMouseOutForQuantityBar}>
        {removeItemFromCartButton}
        {quantityButton}
        {quickAddToCartButton}
      </div>
    ) : <></>

  },[displayQuantityBar, quickAddToCartButton, quantityButton, countInCart, removeItemFromCartButton, handleMouseInForQuantityBar, handleMouseOutForQuantityBar]);

  const editAndQuantityButtons = useMemo(() => {
    return !displayQuantityBar ?
      <>
        {countInCart >= 1 ? editCartQuantity : <></>}
        {quantityButton}
      </>
     : <></>
  }, [displayQuantityBar, editCartQuantity, quantityButton, countInCart])

  const listing = <>
    <a href={fakeLinkHref} className={classes} role="link" data-cy={isAvailable && "menu-item"} onClick={productModalOnClick}>
      {productImage}
      {productDetails}
      {unitPriceLabel}

      <div className="product-footer">
        {isOrderable(product) && !displayQuantityBar ? productPrice : <></>}
        {!isOrderable(product) ? outOfStockOrRestrictionBadge : <></>}
        {isAvailable && isOrderable(product) && !displayQuantityBar && countInCart == 0 ? quickAddToCartButton : <></>}
      </div>

      {isOrderable(product) && countInCart > 0 ? (
      <div className={`quick-quantity-controls is-flexbox align-items-center ${displayQuantityBar ? 'justify-content-between' : 'justify-content-end'}`}>
        {displayQuantityOptions}

        {countInCart > 0 && !displayQuantityBar ? editAndQuantityButtons : <></>}
      </div>) : <></>}
    </a>
  </>

  return (hideOutOfStockProductListing ? <></> : listing);
};
